import { Box, Typography, FormControl, FormControlLabel, Tooltip, Icon, Grid, Checkbox, Container } from '@mui/material';
import { useFormikContext } from 'formik';
import { ToolTipPlacement } from '../../types/componentTypes/tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { useEffect, useMemo, useState } from 'react';
import { convertToMoment, formatDate, formatUtcTimestampToDate, getUTCDate, isFutureDate, isPastDate, isValidDate } from '../../utils/dateUtils';

interface PrizeTeaserProps {
    label?: string;
    containerSpacing?: number;
    visibleFromDateColumns?: number;
    hasPrizeOptionChanged?: boolean;
    disabled?: boolean;
}

const PrizeTeaser = ({
    label,
    hasPrizeOptionChanged,
    containerSpacing = 1,
    visibleFromDateColumns = 11,
    disabled = false,
}: PrizeTeaserProps) => {

    const { values, setFieldValue } = useFormikContext<any>();
    const [isTeaserVisible, setIsTeaserVisible] = useState<boolean>(false);
    const [teaserDateValue, setTeaserDateValue] = useState<any>(null);

    const { timezone, currentDate, prizeStartDate, hasTeaser } = useMemo(() => {
        const timezone = values?.tempParams.config_timezone;
        const currentDate = getUTCDate();
        const prizeStartDate = formatUtcTimestampToDate(values?.tempParams.start_date);
        const hasTeaser = !!values?.visible_from_date;
        return { timezone, currentDate, prizeStartDate, hasTeaser };
    }, [values?.tempParams.config_timezone, values?.tempParams.start_date, values?.visible_from_date]);

    useEffect(() => {
        setIsTeaserVisible(hasTeaser);
    }, [hasTeaser]);

    useEffect(() => {
        if (hasPrizeOptionChanged) {
            const newTeaserDateValue = getTeaserDateValue(values?.visible_from_date, currentDate, timezone);
            setTeaserDateValue(newTeaserDateValue);
        }
    }, [values.visible_from_date, currentDate, timezone, hasPrizeOptionChanged]);

    const isTeaserDisabled = disabled || isPastDate(values?.tempParams.start_date, currentDate) || !isFutureDate(values?.tempParams.start_date, currentDate);
    const minDate =  convertToMoment(currentDate, timezone);
    const maxDate =  convertToMoment(prizeStartDate, timezone);

    const handleOnDateChange = (date: any) => {
        if (date && isValidDate(date)) {
            setFieldValue('visible_from_date', formatDate(date));
            setTeaserDateValue(date);
        } else {
            setFieldValue('visible_from_date', '');
            setTeaserDateValue('');
        }
    }

    const getTeaserDateValue = (teaserDate: string, startDate: string, timezone: string) => {
        if (!teaserDate) {
            return convertToMoment(startDate, timezone);
        }
        return convertToMoment(teaserDate, timezone);
    };

    const handleTeaserDatepicker = (e: React.ChangeEvent<HTMLInputElement> | null) => {
        setIsTeaserVisible(prevState => {
            const newState = !prevState;
            if (!newState) {
                setFieldValue('visible_from_date', '');
                setTeaserDateValue('');
            } else {
                const defaultDate = formatDate(currentDate);
                setFieldValue('visible_from_date', defaultDate);
                setTeaserDateValue(convertToMoment(currentDate, timezone));
            }
            return newState;
        });
    }

    const preventKeyboardInput = (e: React.KeyboardEvent) => {
        e.preventDefault();
    };

    return (
        <Container disableGutters>
             <FormControl disabled={isTeaserDisabled}>
            <Box>
                <FormControlLabel
                    value={'prizeTeaser'}
                    control={
                        <Checkbox
                            className='prize-teaser-checkbox'
                            disabled={isTeaserDisabled}
                            checked={isTeaserVisible}
                            onChange={handleTeaserDatepicker}
                        />
                    }
                    label={<Typography variant='body1'>{label}</Typography>}
                />
                <Tooltip
                    arrow
                    data-testid={`${label}-tooltip`}
                    sx={{ marginTop: '-2px', marginLeft: '-8px', display:'inline-flex !important' }}
                    classes={{ tooltip: 'tooltip_text' }}
                    title={`Show a prize teaser ahead of the start date to build awareness. Once the prize starts, this option is automatically turned off.`}
                   placement={ToolTipPlacement.Bottom}>
                    <Icon
                        data-testid={`prizeTeaser-tooltip-icon`}
                        className='icon-style'
                        fontSize={'large'}>
                        <HelpOutlineIcon />
                    </Icon>
                </Tooltip>
            </Box>
            {
                !isTeaserDisabled && isTeaserVisible &&
                <Grid container spacing={containerSpacing}>
                    <Grid item xs={visibleFromDateColumns}>

                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DesktopDateTimePicker
                                label={'Visible From'}
                                minDate={minDate}
                                maxDate={maxDate}
                                ampm={false}
                                timeSteps={{ minutes: 1 }}
                                value={teaserDateValue || getTeaserDateValue(values?.visible_from_date, currentDate, timezone)}
                                ampmInClock={false}
                                closeOnSelect={false}
                                formatDensity='spacious'
                                orientation='landscape'
                                onChange={handleOnDateChange}
                                slotProps={{
                                    textField: {
                                        onKeyDown: preventKeyboardInput,
                                        className: 'input-text',
                                    },
                                }}

                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>

            }
            </FormControl >
        </Container>

    );
}

export { PrizeTeaser };
